import React, { useEffect, useState } from 'react';
import { Box, Divider, Slide, Tab, Tabs } from '@material-ui/core';
import { Header } from './Header';
import { ContactInformation } from './ContactInformation';
import { ChatMedia } from './ChatMedia';
import { OptionsMenu } from './OptionsMenu';
import { MediaGallery } from './MediaGallery';
import { ContactCallHistory } from './ContactCallHistory';
import { ThreadConversation } from '../../../types/conversation';
import { useTranslation } from 'react-i18next';

export type TDetailsTab = 'info' | 'calls';

export const Details = ({
    conversation,
    onClose,
    onConversationDeleted,
    initialTab = 'info',
}: {
    conversation: ThreadConversation;
    onClose: () => void;
    onConversationDeleted?: () => void;
    initialTab?: TDetailsTab;
}) => {
    const { _id, name, image, phone, linkedContactId, isSilenced, normalizedPhone, contactCountryCode, storeCountryCode } =
        conversation;

    const [view, setView] = useState<'details' | 'gallery'>('details');
    const [activeTab, setActiveTab] = useState<TDetailsTab>(initialTab);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab, _id]);

    const handleOpenGallery = () => setView('gallery');
    const handleCloseGallery = () => setView('details');
    const { t } = useTranslation();

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 'calc(100vh - 80px)',
                flex: 1,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Slide direction="down" in={true} timeout={600}>
                <Box
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Header onClose={onClose} />

                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            display: 'grid',
                            gridTemplateRows: 'auto auto minmax(0, 1fr)',
                        }}
                    >
                        <Box px={3} pt={3} pb={2}>
                            <ContactInformation
                                name={name}
                                image={image}
                                phone={phone}
                                normalizedPhone={normalizedPhone}
                                contactCountryCode={contactCountryCode}
                                storeCountryCode={storeCountryCode}
                            />
                        </Box>

                        <Box borderBottom="1px solid" borderColor="divider">
                            <Tabs
                                value={activeTab}
                                onChange={(_, value) => setActiveTab(value)}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="fullWidth"
                            >
                                <Tab value="info" label={`${t('Chat.Info')}`} />
                                <Tab
                                    value="calls"
                                    label={`${t('Chat.CallHistory')}`}
                                />
                            </Tabs>
                        </Box>

                        <Box
                            px={3}
                            py={3}
                            minHeight={0}
                            style={{ overflowY: 'auto' }}
                        >
                            {activeTab === 'info' && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    style={{ gap: 20 }}
                                >
                                    <ChatMedia
                                        conversationId={_id}
                                        onClick={handleOpenGallery}
                                        enabled={view === 'details'}
                                    />

                                    <Divider />

                                    <OptionsMenu
                                        conversationId={_id}
                                        isSilenced={isSilenced}
                                        onConversationDeleted={
                                            onConversationDeleted
                                        }
                                    />
                                </Box>
                            )}

                            {activeTab === 'calls' && (
                                <ContactCallHistory
                                    contactId={linkedContactId}
                                    conversationId={_id}
                                />
                            )}
                        </Box>
                    </div>
                </Box>
            </Slide>

            <Slide
                direction="left"
                in={view === 'gallery'}
                mountOnEnter
                unmountOnExit
                timeout={800}
            >
                <Box
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <MediaGallery
                        conversationId={_id}
                        onClose={handleCloseGallery}
                    />
                </Box>
            </Slide>
        </Box>
    );
};
